import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import {PRODUCED_PRODUCTS, PRODUCED_PRODUCTS_INGREDIENTS} from "@/modules/ProducedProduct/_store";
// import moment from 'moment';

interface PrescriptManagerState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    meta: any;
}

const state: PrescriptManagerState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    meta: null,
};

export const ITEMS_LIST = 'GET_PRESCRIPT_LIST';
export const GET_SCHEMA = 'GET_SCHEMA';
export const PRESCRIPTS = 'GET_PRESCRIPTS';
export const PRESCRIPTS_INGREDIENTS = 'GET_PRESCRIPTS_INGREDIENTS';

export const actions: ActionTree<PrescriptManagerState, RootState> = {
    loadData({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const columns: any = {
                "id": "prescripts.id",
                "name": "vb.name",
                "copy_name": "vb_copy.name",
                "total_weight": "prescripts.total_weight",
                "datetime": "prescripts.datetime",
                "copy_datetime": "prescripts_copy.datetime",
                "created_at": "prescripts.created_at",
                "protein": "prescripts.protein",
                "fats": "prescripts.fats",
                "carbohydrate": "prescripts.carbohydrate",
                "calories": "prescripts.calories",
                "energy": "prescripts.energy",
                "archived_at": "prescripts.archived_at",
                "close_date": "prescripts.close_date",
                "type": "prescripts.type",
            };

            const filter: any[] = [];

            if (query.name) {
                filter.push({ "$ilike": ["prescripts.name", {"$": `%${query.name}%`}] })
            }
            const orders: any[] = [];

            if (query.sortFields) {
                JSON.parse((query.sortFields)).map((item: any) => {
                    orders.push(item.order == 'desc' ? { $desc: [columns[item.field || 'name' ]] } : { $asc: [columns[item.field || 'name']] })
                })
            }

            vm.$db([
                vm.$q(ITEMS_LIST)
                .raw('documents.list')
                .action({
                    "columns": columns,
                    "from": "s:prescripts",
                    "from_as": "prescripts",
                    joins: [
                        {
                            join: 's:voc_bases',
                            type: 'left',
                            as:   'vb',
                            on: {
                                $eq: ['vb.id', 'prescripts.base_id']
                            }
                        },
                        {
                            join: 's:prescripts',
                            type: 'left',
                            as: 'prescripts_copy',
                            on: {
                              $eq: ['prescripts_copy.id', 'prescripts.copy_id']
                            }
                        },
                        {
                            join: 's:voc_bases',
                            type: 'left',
                            as:   'vb_copy',
                            on: {
                                $eq: ['vb_copy.id', 'prescripts_copy.base_id']
                            }
                        },
                    ],
                    page_size: 10,
                    ...({page: parseInt(query.page) || 1}),
                    ...({
                        order: orders
                    }),
                    ...(
                        filter.length > 1 ?
                        {
                            "where": {
                                "$and": filter
                            }
                        } : {
                            "where": filter[0]
                        }
                    )

                }),
                vm.$q(GET_SCHEMA)
                .raw('schemas.get')
                .action({
                    id: `s:prescripts`
                }),
            ]).then(((result: ServerResult) => {
                commit('SET_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                commit('SET_SCHEMA', result.getOperation(GET_SCHEMA).getContent().schema);
                commit('LOADED_SUCCESS');

                resolve();
            }));
        })
    },

    saveTodoType({ commit, getters, dispatch }, data) {
        const vm = (this as any)._vm;

        const query = data.id ?
            vm.$q('SAVE_DOCUMENT').update(data.id, data) :
            vm.$q('SAVE_DOCUMENT').create('voc_ingredients', data);

        return new Promise((resolve, reject) => {
            vm.$db([
                query
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('SAVE_DOCUMENT');

                if (operationResult.hasError()) {
                    reject();
                } else {
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        });
    },

    copyDocument({ state }, {id}) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q(PRESCRIPTS)
                    .raw('documents.get')
                    .action({
                        id
                    }),
                vm.$q(PRESCRIPTS_INGREDIENTS)
                    .raw('documents.list')
                    .action({
                        columns: {
                            ingredient_id: 'prescripts_ingredients.ingredient_id',
                            weight: 'prescripts_ingredients.weight',
                            percent: 'prescripts_ingredients.percent',
                        },
                        from: 's:prescripts_ingredients',
                        from_as: 'prescripts_ingredients',
                        where: {
                            $eq: ['prescripts_ingredients.prescript_id', {$: id}]
                        }
                    }),
            ]).then((result: ServerResult) => {
                const prescript = result.getOperation(PRESCRIPTS).getContent().document;
                const ingredients = result.getOperation(PRESCRIPTS_INGREDIENTS).getContent().documents;

                prescript.copy_id = prescript.id;
                delete prescript.id;

                const queries: any[] = [
                    vm.$q(PRESCRIPTS)
                        .create('s:prescripts', prescript)
                        .withManyRelation('prescripts_ingredients', 'prescript_id', (createRelated: any) => {
                            return ingredients.map((item: any) => createRelated(item));
                        })
                ]
                vm.$db(queries).then((result: ServerResult) => {
                    resolve(null);
                })
                .catch((error: any) => {
                    console.error('errror', error)
                    reject(error);
                });

            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        });
    },

};

export const mutations: MutationTree<PrescriptManagerState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = data.documents;

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
    SET_SCHEMA(state, schema) {
        state.meta = fetchEnumsFromSchema(schema);
    },
};

const getters = {

};

export const STORE_KEY = '$_prescript_manger';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
